import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Accordion,
  Box,
  Button,
  Field,
  Flex,
  Message,
  Text,
  Tooltip,
  useAppTheme,
} from '@workwhile/ui'
import { Check, Info, X } from 'lucide-react'
import { AccordionCard } from 'components/AccordionCard'
import { useAssessmentDetailContextValue } from '../context/AssessmentDetailProvider'
import {
  createEvaluationFormSchema,
  emptyEvaluationFormValues,
  evaluationStatusSchema,
  EvaluationFormSchema,
  EvaluationStatus,
} from '../schema'
import { ConfirmEvaluationModal } from './ConfirmEvaluationModal'

type ConfirmationValues = {
  questionEvaluations: EvaluationFormSchema['questionEvaluations']
  justifications: EvaluationFormSchema['questionJustifications']
}

const EvaluationButton = ({
  variant,
  onClick,
  isSelected = false,
  size = 36,
}: {
  variant: EvaluationStatus
  onClick: () => void
  isSelected?: boolean
  size?: number
}) => {
  const { colors } = useAppTheme()

  const config = !isSelected
    ? {
        color: colors.lightText,
        backgroundColor: colors.white,
        border: `1px solid ${colors.lightText}`,
        boxShadow: `0 0 0 2px ${colors.offWhite}`,
      }
    : variant === 'pass'
      ? {
          color: colors.primary200,
          backgroundColor: colors.primary50,
          border: `1px solid ${colors.primary200}`,
          boxShadow: `0 0 0 2px ${colors.offWhite}`,
        }
      : {
          color: colors.error200,
          backgroundColor: colors.error50,
          border: `1px solid ${colors.error200}`,
        }

  const Icon = variant === 'pass' ? Check : X
  const iconSize = Math.round(size / 2)

  return (
    <Box
      borderRadius="100%"
      height={size}
      width={size}
      display="flex"
      alignItems="center"
      justifyContent="center"
      onClick={onClick}
      style={{
        cursor: 'pointer',
        backgroundColor: isSelected ? config.backgroundColor : undefined,
        transition: 'background-color 0.2s',
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        ;(e.currentTarget as HTMLDivElement).style.backgroundColor =
          colors.offWhite
      }}
      onMouseLeave={(e) => {
        ;(e.currentTarget as HTMLDivElement).style.backgroundColor = isSelected
          ? config.backgroundColor || ''
          : ''
      }}
      {...config}
    >
      <Icon absoluteStrokeWidth size={iconSize} color={config.color} />
    </Box>
  )
}

const UNEVALUABLE_STATUSES = new Set(['new', 'incomplete', 'in_progress'])

export const EvaluationPanel = () => {
  const { colors } = useAppTheme()
  const {
    state: { requirements, status },
  } = useAssessmentDetailContextValue()

  const [confirmation, setConfirmation] = useState<ConfirmationValues | null>(
    null
  )

  const questionIds = requirements.flatMap((requirement) =>
    requirement.questions.map((question) => question.id)
  )

  const {
    watch,
    setValue,
    getValues,
    register,
    handleSubmit,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm<EvaluationFormSchema>({
    resolver: zodResolver(createEvaluationFormSchema(questionIds)),
    defaultValues: emptyEvaluationFormValues(),
  })

  const requirementEvaluations = watch('requirementEvaluations')
  const questionEvaluations = watch('questionEvaluations')

  if (status && UNEVALUABLE_STATUSES.has(status)) {
    const statusMessage =
      status === 'in_progress' || status === 'incomplete'
        ? "has started but hasn't been completed yet"
        : "hasn't started yet"

    return (
      <AccordionCard title="Evaluation" size="large">
        <Message variant="info" borderRadius="small">
          {`This assessment ${statusMessage} and therefore can't be evaluated.`}
        </Message>
      </AccordionCard>
    )
  }

  // Pass All / Fail All: set (or clear) every question under the requirement.
  const handleRequirementEvaluationClick = (
    requirementId: number,
    questionIdsForRequirement: number[],
    evaluationStatus: EvaluationStatus
  ) => {
    clearErrors('questionEvaluations')
    const requirementKey = String(requirementId)
    const requirementEvaluations = { ...getValues('requirementEvaluations') }
    const questionEvaluations = { ...getValues('questionEvaluations') }

    // Clicking the already-selected bulk button clears it and its questions.
    const isDeselecting =
      requirementEvaluations[requirementKey] === evaluationStatus

    if (isDeselecting) {
      delete requirementEvaluations[requirementKey]
      questionIdsForRequirement.forEach((questionId) => {
        delete questionEvaluations[String(questionId)]
      })
    } else {
      requirementEvaluations[requirementKey] = evaluationStatus
      // Cascade the bulk choice onto each child question.
      questionIdsForRequirement.forEach((questionId) => {
        questionEvaluations[String(questionId)] = evaluationStatus
      })
    }

    setValue('requirementEvaluations', requirementEvaluations)
    setValue('questionEvaluations', questionEvaluations)
  }

  // Per-question toggle. Does not update sibling questions.
  const handleQuestionEvaluationClick = (
    requirementId: number,
    questionId: number,
    evaluationStatus: EvaluationStatus
  ) => {
    clearErrors('questionEvaluations')
    const requirementKey = String(requirementId)
    const questionKey = String(questionId)
    const questionEvaluations = { ...getValues('questionEvaluations') }
    const isDeselecting = questionEvaluations[questionKey] === evaluationStatus

    if (isDeselecting) {
      delete questionEvaluations[questionKey]
    } else {
      questionEvaluations[questionKey] = evaluationStatus
    }

    const bulkStatus = getValues('requirementEvaluations')[requirementKey]
    // If this click diverges from Pass/Fail All, clear the parent selection.
    // Re-aligning the question later does not restore the parent button.
    const breaksBulk =
      bulkStatus != null && (isDeselecting || evaluationStatus !== bulkStatus)

    if (breaksBulk) {
      const requirementEvaluations = {
        ...getValues('requirementEvaluations'),
      }
      delete requirementEvaluations[requirementKey]
      setValue('requirementEvaluations', requirementEvaluations)
    }

    setValue('questionEvaluations', questionEvaluations)
  }

  const onSubmit = handleSubmit((data) => {
    setConfirmation({
      questionEvaluations: data.questionEvaluations,
      justifications: data.questionJustifications,
    })
  })

  return (
    <>
      <AccordionCard title="Evaluation" size="large">
        <Box as="form" onSubmit={onSubmit}>
          <Flex flexDirection="column">
            {requirements.map((requirement, index) => {
              const questionIdsForRequirement = requirement.questions.map(
                (question) => question.id
              )
              const requirementStatus =
                requirementEvaluations[String(requirement.id)]
              return (
                <Flex
                  key={requirement.id}
                  backgroundColor={
                    index % 2 === 0 ? colors.offWhite : 'transparent'
                  }
                  borderRadius="standard"
                  flexDirection="column"
                  p={2}
                  gap={2}
                >
                  <Flex
                    flexDirection="row"
                    gap={3}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box flex={1}>
                      <Text variant="bodyMedium">{requirement.name}</Text>
                    </Box>
                    <Flex justifyContent="flex-end" gap={2} alignItems="center">
                      <Button
                        type="button"
                        kind="small"
                        variant={
                          requirementStatus === 'pass' ? 'primary' : 'secondary'
                        }
                        onClick={() =>
                          handleRequirementEvaluationClick(
                            requirement.id,
                            questionIdsForRequirement,
                            'pass'
                          )
                        }
                      >
                        Pass All
                      </Button>
                      <Button
                        type="button"
                        kind="small"
                        danger
                        variant={
                          requirementStatus === 'fail' ? 'primary' : 'secondary'
                        }
                        style={
                          requirementStatus === 'fail'
                            ? {
                                backgroundColor: colors.error,
                                color: colors.white,
                              }
                            : undefined
                        }
                        onClick={() =>
                          handleRequirementEvaluationClick(
                            requirement.id,
                            questionIdsForRequirement,
                            'fail'
                          )
                        }
                      >
                        Fail All
                      </Button>
                    </Flex>
                  </Flex>

                  {requirement.questions.map((question) => (
                    <Flex
                      key={question.id}
                      flexDirection="column"
                      gap={1}
                      pl={3}
                    >
                      <Flex
                        flexDirection="row"
                        gap={3}
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Flex flex={1} alignItems="center" gap={1}>
                          <Text fontSize={1}>{question.name}</Text>
                          <Tooltip
                            content={
                              <Flex
                                flexDirection="column"
                                gap={2}
                                style={{ maxWidth: 320 }}
                              >
                                <Box>
                                  <Text variant="bodyMedium" fontSize={1}>
                                    Evaluation Criteria
                                  </Text>
                                  <Text fontSize={1}>
                                    {question.evaluationCriteria || '—'}
                                  </Text>
                                </Box>
                                <Box>
                                  <Text variant="bodyMedium" fontSize={1}>
                                    Ideal Answer
                                  </Text>
                                  <Text fontSize={1}>
                                    {question.idealAnswer || '—'}
                                  </Text>
                                </Box>
                              </Flex>
                            }
                          >
                            <Info size={14} />
                          </Tooltip>
                        </Flex>
                        <Flex
                          justifyContent="flex-end"
                          gap={2}
                          alignItems="center"
                        >
                          {evaluationStatusSchema.options.map(
                            (evaluationStatus) => (
                              <EvaluationButton
                                key={`${question.id}-${evaluationStatus}`}
                                variant={evaluationStatus}
                                size={28}
                                isSelected={
                                  questionEvaluations[String(question.id)] ===
                                  evaluationStatus
                                }
                                onClick={() =>
                                  handleQuestionEvaluationClick(
                                    requirement.id,
                                    question.id,
                                    evaluationStatus
                                  )
                                }
                              />
                            )
                          )}
                        </Flex>
                      </Flex>
                      <Accordion
                        items={[
                          {
                            id: `justification-${question.id}`,
                            trigger: (
                              <Text color="primary" fontSize={1}>
                                Justification
                              </Text>
                            ),
                            content: (
                              <Field>
                                <Box
                                  as="textarea"
                                  id={`assessment-justification-${question.id}`}
                                  placeholder="Justification (optional)"
                                  rows={2}
                                  width="100%"
                                  p={2}
                                  borderRadius="small"
                                  borderColor={colors.neutrals[100]}
                                  style={{
                                    resize: 'vertical',
                                    minHeight: 48,
                                    font: 'inherit',
                                    borderWidth: 1,
                                    borderStyle: 'solid',
                                  }}
                                  {...register(
                                    `questionJustifications.${question.id}`
                                  )}
                                />
                              </Field>
                            ),
                          },
                        ]}
                      />
                    </Flex>
                  ))}
                </Flex>
              )
            })}

            {errors.questionEvaluations?.message ? (
              <Message variant="error" mt={3} borderRadius="small">
                {errors.questionEvaluations.message}
              </Message>
            ) : null}

            <Flex mt={3} justifyContent="flex-end">
              <Button type="submit">Submit</Button>
            </Flex>
          </Flex>
        </Box>
      </AccordionCard>

      <ConfirmEvaluationModal
        open={confirmation != null}
        questionEvaluations={confirmation?.questionEvaluations ?? {}}
        justifications={confirmation?.justifications ?? {}}
        onClose={() => setConfirmation(null)}
        onSuccess={() => {
          setConfirmation(null)
          reset(emptyEvaluationFormValues())
        }}
      />
    </>
  )
}
